import React from "react";
import { Modal, Form, Button, Dropdown, ButtonGroup } from "react-bootstrap";
import { Event, EventType } from "../types/Types";
import { eventTypeOptions, getEventTypeLabel } from "./eventDisplay";

interface EventModalProps {
  show: boolean;
  editingEvent: Event | null;
  eventTitle: string;
  eventType: EventType | '';
  eventDescription: string;
  scheduledAt: string;
  eventCompleted: boolean;
  saving: boolean;
  onHide: () => void;
  onSave: () => void;
  onTitleChange: (value: string) => void;
  onTypeChange: (value: EventType) => void;
  onDescriptionChange: (value: string) => void;
  onScheduledAtChange: (value: string) => void;
  onCompletedChange: (value: boolean) => void;
}

const EventModal: React.FC<EventModalProps> = ({
  show,
  editingEvent,
  eventTitle,
  eventType,
  eventDescription,
  scheduledAt,
  eventCompleted,
  saving,
  onHide,
  onSave,
  onTitleChange,
  onTypeChange,
  onDescriptionChange,
  onScheduledAtChange,
  onCompletedChange,
}) => {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header>
        <Modal.Title>{editingEvent ? 'Edit Event' : 'Create Event'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Event Title <span style={{ color: 'red' }}>*</span></Form.Label>
            <Form.Control
              type="text"
              value={eventTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="For example: Pub name and town"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Event Type <span style={{ color: 'red' }}>*</span></Form.Label>
            <Dropdown className="w-100">
              <Dropdown.Toggle 
                variant="light" 
                className="w-100 text-start form-control"
                style={{ 
                  backgroundColor: 'white',
                  color: 'black',
                  borderColor: '#ced4da',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{getEventTypeLabel(eventType)}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100">
                {eventTypeOptions.map((type) => (
                  <Dropdown.Item key={type} onClick={() => onTypeChange(type)}>
                    {getEventTypeLabel(type)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Scheduled Date</Form.Label>
            <Form.Control
              type="date"
              value={scheduledAt}
              onChange={(e) => onScheduledAtChange(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={eventDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Optional description, for example: Pub address"
            />
          </Form.Group>

          {editingEvent && (
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <Form.Label className="mb-0">Event Completed</Form.Label>
                <ButtonGroup size="sm">
                  <Button
                    variant={!eventCompleted ? "success" : "outline-success"}
                    onClick={() => onCompletedChange(false)}
                    active={!eventCompleted}
                  >
                    No
                  </Button>
                  <Button
                    variant={eventCompleted ? "success" : "outline-success"}
                    onClick={() => onCompletedChange(true)}
                    active={eventCompleted}
                  >
                    Yes
                  </Button>
                </ButtonGroup>
              </div>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onSave} disabled={saving || !eventType || !eventTitle}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventModal;
